<template>
  <div class="page antialiased">
    <div
      class="tw:relative tw:min-h-screen"
      :class="openNav ? 'tw:h-dvh tw:overflow-hidden tw:md:min-h-screen tw:md:h-auto tw:md:overflow-visible' : ''">
      <DashboardSidebar
        :open-nav="openNav"
        :navigation-data="navigationData"
        :show-budget="showBudget"
        :show-profile="props.showProfile"
        @toggle-nav="openNav = !openNav"
        @logout="logoutUser" />

      <div class="lead-wrapper tw:md:ml-80">
        <div class="wrapper">
          <div class="page-wrapper">
            <router-view></router-view>
          </div>
        </div>
        <!-- <div style="height: 120px"></div> -->
        <!-- <Footer></Footer> -->
      </div>
    </div>

    <!-- Autopilot Bar -->
    <AutopilotBar />
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { getMainNavigationElements } from '../router/main_nav';
import DashboardSidebar from '@/components/header/DashboardSidebar.vue';
import translator from '@/locale/translator.js';
import AutopilotBar from '@/components/autopilot/AutopilotBar.vue';

import { useCustomerFeaturesStore } from '@/stores/customerFeatures';

import type { MainNavElement } from '@/router/main_nav';
import { IconFileCertificate, IconBrain, IconBox, IconBriefcase } from '@tabler/icons-vue';
import { destroySession } from '@/api/client';
import { fetchActiveBudget } from '@/api/budget';

const props = withDefaults(defineProps<{ showProfile?: boolean }>(), {
  showProfile: true,
});

const featureStore = useCustomerFeaturesStore();

// Manually trigger the navbar because the bootstrap version didn't work properly
const openNav = ref(true);
// in case this is a mobile screen, hide the navbar by default (the page has to be reloaded to read the screen size)
const width = window.innerWidth > 0 ? window.innerWidth : screen.width;
if (width < 768) {
  openNav.value = false;
}

const baseNavigationData = getMainNavigationElements();
const showBudget = ref(false);

const navigationData = computed(() => {
  const additionalNavElements: MainNavElement[] = [];

  if (featureStore.hasFeature('order_management')) {
    additionalNavElements.push({
      label: translator.get('nav.orders'),
      icon: IconBox,
      to: { path: '/dashboard/orders' },
      active: false,
    });
  }

  const currentCampaignId = featureStore.features.poolmirror_campaigns?.active_campaign_ids?.toString();
  if (featureStore.hasFeature('poolmirror_campaigns') && currentCampaignId) {
    const exploded = currentCampaignId.split(',');
    let path = `/dashboard/poolmirror/${currentCampaignId}/detail`;
    if (exploded.length > 1) {
      path = `/dashboard/poolmirror`;
    }
    additionalNavElements.push({
      label: translator.get('nav.active_poolmirror_campaign'),
      icon: IconFileCertificate,
      to: { path },
      active: false,
    });
  }

  if (featureStore.hasFeature('promptsuite')) {
    additionalNavElements.push({
      label: translator.get('nav.promptsuite'),
      icon: IconBrain,
      to: { path: `/dashboard/promptsuite/apps` },
      active: false,
    });
  }

  if (featureStore.hasFeature('cvanalyzer')) {
    additionalNavElements.push({
      label: translator.get('nav.cvanalyzer'),
      icon: IconBrain,
      to: { path: `/dashboard/cvanlayzer` },
      active: false,
    });
  }

  if (featureStore.hasFeature('external_job_market')) {
    additionalNavElements.push({
      label: translator.get('nav.external_job_markets'),
      icon: IconBriefcase,
      to: { path: `/dashboard/external-job-markets` },
      active: false,
    });
  }

  return [...baseNavigationData, ...additionalNavElements];
});

fetchActiveBudget({ resolve: 'contracts' }).then((res) => {
  try {
    if (res?.data) {
      showBudget.value = true;
    }
  } catch (error) {
    console.error('Error fetching active budget:', error);
    showBudget.value = false;
  }
});

const logoutUser = () => {
  destroySession();
  // navigate to a plain /login (not just reload the current URL) so App.vue's
  // boot flow doesn't capture the page we're leaving as a post-login redirect target
  location.href = '/login';
};
</script>

<style scoped>
.page-wrapper {
  height: 100vh;
}

.page-wrapper > div {
  flex: 1;
  display: flex;
  flex-direction: column;
}
</style>
